/**
 * LPE1 Leasehold Property Enquiries — Disclosure Questions
 *
 * Only shown when tenure === 'leasehold'.
 * LPE1 is normally completed by the managing agent or freeholder,
 * but sellers can pre-fill what they know to speed up the process.
 * Fields marked preFillable may be populated from uploaded documents.
 */

export type LPE1AnswerType = 'yesNoNotKnown' | 'text' | 'yesNo';

export interface LPE1Question {
  id: string;
  section: number;
  sectionTitle: string;
  questionText: string;
  helpText?: string;
  answerType: LPE1AnswerType;
  conditionalOn?: { questionId: string; answer: string };
  /** Can be pre-filled from uploaded lease or service charge documents */
  preFillable?: boolean;
}

export interface LPE1Section {
  section: number;
  title: string;
  questions: LPE1Question[];
}

const lpe1Questions: LPE1Question[] = [
  // ── Section 1: Ground rent ─────────────────────────────────────────
  {
    id: 'lpe1-1.1',
    section: 1,
    sectionTitle: 'Ground rent',
    questionText: 'What is the current annual ground rent?',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-1.2',
    section: 1,
    sectionTitle: 'Ground rent',
    questionText: 'When is the ground rent due?',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-1.3',
    section: 1,
    sectionTitle: 'Ground rent',
    questionText: 'Is the ground rent paid up to date?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 2: Service charge ──────────────────────────────────────
  {
    id: 'lpe1-2.1',
    section: 2,
    sectionTitle: 'Service charge',
    questionText: 'What is the current annual service charge?',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-2.2',
    section: 2,
    sectionTitle: 'Service charge',
    questionText: 'Is the service charge paid up to date?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-2.3',
    section: 2,
    sectionTitle: 'Service charge',
    questionText: 'Are there any anticipated increases to the service charge?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-2.4',
    section: 2,
    sectionTitle: 'Service charge',
    questionText: 'Are the last 3 years\' service charge accounts available?',
    helpText: 'Buyers and their solicitors will typically request these. If you have them, you can upload them in the documents step.',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 3: Building insurance ──────────────────────────────────
  {
    id: 'lpe1-3.1',
    section: 3,
    sectionTitle: 'Building insurance',
    questionText: 'Who arranges the building insurance?',
    helpText: 'Usually the freeholder or management company.',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-3.2',
    section: 3,
    sectionTitle: 'Building insurance',
    questionText: 'What is the current sum insured for the building?',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-3.3',
    section: 3,
    sectionTitle: 'Building insurance',
    questionText: 'Have there been any building insurance claims in the last 5 years?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-3.3a',
    section: 3,
    sectionTitle: 'Building insurance',
    questionText: 'Please give details of the insurance claims.',
    answerType: 'text',
    conditionalOn: { questionId: 'lpe1-3.3', answer: 'yes' },
  },

  // ── Section 4: Management company ──────────────────────────────────
  {
    id: 'lpe1-4.1',
    section: 4,
    sectionTitle: 'Management company',
    questionText: 'What is the name and address of the managing agent?',
    answerType: 'text',
    preFillable: true,
  },
  {
    id: 'lpe1-4.2',
    section: 4,
    sectionTitle: 'Management company',
    questionText: 'Is the management company a resident-owned company (RTM or share of freehold)?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-4.3',
    section: 4,
    sectionTitle: 'Management company',
    questionText: 'Are the management company\'s accounts available for inspection?',
    helpText: 'Leaseholders have a legal right to inspect the management company\'s accounts. If available, you can upload them in the documents step.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-4.4',
    section: 4,
    sectionTitle: 'Management company',
    questionText: 'Are you aware of any financial concerns about the management company?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 5: Major works ─────────────────────────────────────────
  {
    id: 'lpe1-5.1',
    section: 5,
    sectionTitle: 'Major works',
    questionText: 'Are any major works planned or in progress?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-5.1a',
    section: 5,
    sectionTitle: 'Major works',
    questionText: 'Please describe the works and estimated cost.',
    answerType: 'text',
    conditionalOn: { questionId: 'lpe1-5.1', answer: 'yes' },
  },
  {
    id: 'lpe1-5.2',
    section: 5,
    sectionTitle: 'Major works',
    questionText: 'Is there a reserve or sinking fund for major works?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 6: Disputes and proceedings ────────────────────────────
  {
    id: 'lpe1-6.1',
    section: 6,
    sectionTitle: 'Disputes and proceedings',
    questionText: 'Are there any current or pending legal proceedings involving the management company or freeholder?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-6.1a',
    section: 6,
    sectionTitle: 'Disputes and proceedings',
    questionText: 'Please give details of the proceedings.',
    answerType: 'text',
    conditionalOn: { questionId: 'lpe1-6.1', answer: 'yes' },
  },

  // ── Section 7: Lease variations ────────────────────────────────────
  {
    id: 'lpe1-7.1',
    section: 7,
    sectionTitle: 'Lease variations',
    questionText: 'Have there been any variations or amendments to the original lease?',
    helpText: 'This includes any deeds of variation, supplemental leases, or formal changes to the lease terms.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'lpe1-7.1a',
    section: 7,
    sectionTitle: 'Lease variations',
    questionText: 'Please describe the lease variations.',
    answerType: 'text',
    conditionalOn: { questionId: 'lpe1-7.1', answer: 'yes' },
  },
];

export function getLPE1Sections(): LPE1Section[] {
  const sectionMap = new Map<number, LPE1Section>();
  for (const q of lpe1Questions) {
    if (!sectionMap.has(q.section)) {
      sectionMap.set(q.section, { section: q.section, title: q.sectionTitle, questions: [] });
    }
    sectionMap.get(q.section)!.questions.push(q);
  }
  return Array.from(sectionMap.values()).sort((a, b) => a.section - b.section);
}

export function getAllLPE1Questions(): LPE1Question[] {
  return lpe1Questions;
}

export function countVisibleLPE1Questions(answers: Record<string, string>): number {
  return lpe1Questions.filter(q => {
    if (!q.conditionalOn) return true;
    return answers[q.conditionalOn.questionId] === q.conditionalOn.answer;
  }).length;
}

export function countAnsweredLPE1Questions(answers: Record<string, string>): number {
  return lpe1Questions.filter(q => {
    if (!q.conditionalOn) return !!answers[q.id];
    if (answers[q.conditionalOn.questionId] !== q.conditionalOn.answer) return false;
    return !!answers[q.id];
  }).length;
}
